import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Icon, Link, useBreakpointBetween} from '@bonprix/design-system';

const VISIBLE_ROWS = 2;

/**
 * Shared layout for the recent/popular "search tags": a header slot, a wrapping row of tag chips, and
 * (on mobile) a collapse to two rows behind a "mehr/weniger anzeigen" toggle; desktop shows all.
 *
 * Each chip is supplied by the caller via `renderChip(term, isHidden)` so RecentSearches (term + insert
 * arrow) and PopularSearches (single term button) can differ — the collapse/overflow measurement and the
 * toggle live here only. `terms` is expected pre-sliced to the max the caller wants to show.
 */
export const SearchTagList = ({header, terms, showMoreLabel, showLessLabel, renderChip, onToggleExpanded}) => {
    const items = terms || [];
    // Mobile (<992px) collapses to two rows behind a toggle; desktop shows all at once.
    const isMobile = useBreakpointBetween('s', 'xl');
    const tagRefs = useRef([]);
    const [expanded, setExpanded] = useState(false);
    const [overflow, setOverflow] = useState(false);
    const [collapsedHeight, setCollapsedHeight] = useState(null);
    const [hiddenFromIndex, setHiddenFromIndex] = useState(items.length);

    const measure = useCallback(() => {
        const tags = tagRefs.current.filter(Boolean);
        if (!isMobile || tags.length === 0) {
            setOverflow(false);
            setCollapsedHeight(null);
            setHiddenFromIndex(tags.length);
            return;
        }
        const tops = tags.map((el) => el.offsetTop);
        const distinctTops = [...new Set(tops)].sort((a, b) => a - b);
        if (distinctTops.length > VISIBLE_ROWS) {
            const thirdRowTop = distinctTops[VISIBLE_ROWS];
            setOverflow(true);
            setCollapsedHeight(thirdRowTop - distinctTops[0]);
            setHiddenFromIndex(tops.findIndex((top) => top >= thirdRowTop));
        } else {
            setOverflow(false);
            setCollapsedHeight(null);
            setHiddenFromIndex(tags.length);
        }
    }, [isMobile]);

    useLayoutEffect(() => {
        measure();
    }, [measure, items.length]);

    useEffect(() => {
        const handleResize = () => measure();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [measure]);

    if (items.length === 0) {
        return null;
    }

    const collapsed = overflow && !expanded;
    const toggleExpanded = () => {
        const next = !expanded;
        setExpanded(next);
        onToggleExpanded?.(next);
    };

    return (
        <div>
            {header}
            <div
                className={`flex flex-wrap gap-x-2${collapsed ? ' overflow-hidden' : ''}`}
                style={collapsed && collapsedHeight != null ? {maxHeight: `${collapsedHeight}px`} : undefined}
                role="list"
            >
                {items.map((term, index) => {
                    const isHidden = collapsed && index >= hiddenFromIndex;
                    return (
                        <div
                            key={`${term}-${index}`}
                            ref={(el) => (tagRefs.current[index] = el)}
                            className="flex items-center py-1.5 max-w-full"
                            role="listitem"
                            aria-hidden={isHidden ? 'true' : undefined}
                        >
                            {renderChip(term, isHidden, index)}
                        </div>
                    );
                })}
            </div>
            {overflow && (
                <div className="mt-2 inline-flex items-center gap-1">
                    <Link small component="button" onClick={toggleExpanded}>
                        {expanded ? showLessLabel || 'weniger anzeigen' : showMoreLabel || 'mehr anzeigen'}
                    </Link>
                    <span
                        className="inline-flex items-center cursor-pointer [&_svg]:w-4 [&_svg]:h-4"
                        onClick={toggleExpanded}
                        onMouseDown={(e) => e.preventDefault()}
                        aria-hidden="true"
                    >
                        <Icon variant={expanded ? 'arrow-up' : 'arrow-down'} />
                    </span>
                </div>
            )}
        </div>
    );
};
